import { Button } from 'components/atoms';
import { MagellanPopups } from 'components/molecules';
import { Form } from 'components/organisms';
import { videoInitialValues } from 'components/organisms/forms/video/VideoForm';
import { useHasPermission } from 'hooks';
import { useParamId } from 'hooks/useParamId';
import middleware from 'middleware';
import { PiPencilSimpleLine, PiStorefront } from 'react-icons/pi';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { PopupActions } from 'reactjs-popup/dist/types';
import { useDeleteVideo, useGetVideo } from 'services/video';
import { STATUS } from 'services/types';
import { getMainTitle } from 'utils/global/mainTitle.utils';
import { getLastPathname } from 'utils/navbar/getParentPathname';
import { usePostMusicProductDigitalFromVideo } from 'services/music-product-digital';
import { onErrorShowToast } from 'utils/service/errorHandler';

const Video = () => {
  const videoId = useParamId('video');
  const { pathname } = useLocation();
  const { t } = useTranslation();

  const navigate = useNavigate();
  const hasPermission = useHasPermission();

  const removeVideo = useDeleteVideo(videoId);
  const { data: video, isLoading } = useGetVideo(videoId);
  const createSingleFromVideo = usePostMusicProductDigitalFromVideo();

  const onRemoveVideo = async (actions: PopupActions) => {
    await removeVideo?.mutateAsync(undefined, {
      onError: error => {
        actions.close();
        onErrorShowToast(error);
      },
      onSuccess: () => {
        navigate('/video');
      },
    });
  };

  const onCreateSingle = async () => {
    if (!videoId) return;
    await createSingleFromVideo.mutateAsync(
      { videoId },
      {
        onError: error => {
          onErrorShowToast(error);
        },
        onSuccess: productId => {
          if (productId) {
            navigate(`/music-product-digital/${productId}/contracts`);
          }
        },
      }
    );
  };

  return (
    <Form.Video
      i18nextPrefix='video'
      initialValues={
        video ? middleware.video.queryToForm(video) : videoInitialValues
      }
      onSubmit={() => {}}
      tittle={getMainTitle(video?.mainTitle)}
      status={{ isLoading: isLoading, displayMode: true }}
      topbarContent={
        <div className='flex w-full justify-end items-center gap-2'>
          {video?.videoStatus !== STATUS.draft && (
            <Button
              isClear
              disabled={!videoId || createSingleFromVideo.isPending}
              onClick={onCreateSingle}>
              <Button.Icon icon={<PiStorefront />} />
              <Button.Text text={t('video.create-single')} />
            </Button>
          )}
          {hasPermission('VideoPermissionsLIST**', 2) && (
            <Button
              isClear
              onlyIcon
              disabled={!video?.videoLink && !!video?.originalVideoLink}
              onClick={() => {
                video &&
                  navigate(
                    `/video/edit/${videoId}/${getLastPathname(pathname)}`,
                    {
                      replace: true,
                    }
                  );
              }}>
              <Button.Description description={t('button.edit')} />

              <Button.Icon icon={<PiPencilSimpleLine />} />
            </Button>
          )}
          {hasPermission('VideoPermissionsLIST**', 4) && (
            <MagellanPopups.DeleteWarning
              name={video?.mainTitle.title}
              onRemove={onRemoveVideo}
            />
          )}
        </div>
      }
    />
  );
};

export default Video;
